import { useEffect, useRef, useCallback } from 'react';
import { REACT_APP_WS_URL } from '../config/env';
import { createWsBackoff, onNetworkWake } from './wsReconnect';

export type InboxEvent =
  | { type: 'thread_update'; threadId: number; status?: string; folderId?: number | null; assignedTo?: number | null }
  | { type: 'thread_new'; threadId: number }
  | { type: 'thread_deleted'; threadId: number }
  | { type: 'bulk_archive'; threadIds: number[] }
  | { type: 'ticket_update'; ticketId: number; threadId?: number | null; status?: string }
  | { type: 'label_change'; threadId: number }
  | { type: 'folder_change' }
  | { type: 'note_added'; threadId: number }
  | { type: 'presence'; threadId: number; userId: number; userName: string; action: 'viewing' | 'typing' | 'left' }
  | { type: 'refresh' }
  | { type: 'phone.incoming_call'; callSid: string; fromNumber: string; fromName?: string | null; toNumber: string; toUserId: number; ringExpiresAt: string }
  | { type: 'phone.incoming_call_resolved'; callSid: string; status: 'accepted' | 'declined' | 'missed' | 'voicemail'; resolvedByUserId?: number }
  | { type: 'phone.inbound_sms'; conversationId: number | null; threadId: number | null; fromNumber: string; toNumber: string; phoneNumberId: number | null };

function getWsBase(): string {
  if (REACT_APP_WS_URL) return REACT_APP_WS_URL;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

/**
 * Connects to the /ws/inbox WebSocket channel and invokes `onEvent`
 * whenever a server-side inbox mutation occurs.
 *
 * PHONE-A4 (V-12) — reconnection is jittered exponential backoff (1s → 60s,
 * reset to 1s after a socket survives 10s) plus an immediate reconnect when the
 * tab becomes visible or the network comes back. It used to be a flat 5-second
 * retry, forever: a tab with an expired session hammered /ws/inbox with 401s
 * every 5 seconds for as long as it stayed open (visible in production logs),
 * and a laptop waking from sleep could sit with a dead socket for a full cycle.
 * The softphone's incoming-call banner rides THIS channel, so that dead window
 * was a window in which calls reached nobody. See hooks/wsReconnect.ts for why
 * a client cannot simply detect the 401 and stop.
 */
export function useInboxSocket(onEvent: (event: InboxEvent) => void): { send: (msg: any) => void } {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;
  const wsRef = useRef<WebSocket | null>(null);

  const send = useCallback((msg: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify(msg)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    // ONE timer, always cleared before it is re-armed. Two overlapping timers
    // would double the connect rate on every wake — the bug the flat-timer
    // version was one careless edit away from.
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let unmounted = false;
    const backoff = createWsBackoff();

    function schedule(delayMs: number) {
      if (unmounted) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, delayMs);
    }

    function connect() {
      if (unmounted) return;
      try {
        ws = new WebSocket(`${getWsBase()}/ws/inbox`);
        wsRef.current = ws;
      } catch {
        schedule(backoff.nextDelay());
        return;
      }

      ws.onopen = () => { backoff.noteOpen(); };

      ws.onmessage = (e) => {
        try {
          const event: InboxEvent = JSON.parse(e.data);
          cbRef.current(event);
        } catch { /* ignore non-JSON messages */ }
      };

      ws.onclose = () => {
        ws = null;
        wsRef.current = null;
        backoff.noteClosed();
        if (!unmounted) schedule(backoff.nextDelay());
      };

      ws.onerror = () => {
        try { ws?.close(); } catch { /* ignore */ }
      };
    }

    // Waking is only ever a REASON to try sooner, never a second connection:
    // if the socket is already open or still connecting, do nothing at all.
    const stopWake = onNetworkWake(() => {
      if (unmounted) return;
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      backoff.reset();
      schedule(0);
    });

    schedule(500);

    return () => {
      unmounted = true;
      stopWake();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        } catch { /* ignore */ }
        ws = null;
        wsRef.current = null;
      }
    };
  }, []);

  return { send };
}

/**
 * Convenience wrapper — returns a stable function reference you can pass
 * as a dependency-free callback to the hook.
 */
export function useStableCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: any[]) => ref.current(...args), []) as unknown as T;
}
